import { createIndexedDbAdapter, isIndexedDbAvailable } from './adapters/indexedDbAdapter'
import { createMemoryAdapter } from './adapters/memoryAdapter'
import {
  publishChapterBackupPut,
  publishChapterBackupRemove,
  subscribeChapterBackupBroadcast,
} from './broadcast'
import {
  migrateLocalStorageBackups,
  readLegacyLocalStorageBackups,
} from './migrateLocalStorageBackups'
import { requestPersistentStorage } from './persistentStorage'
import {
  isActiveSyncPhase,
  isGraceExpired,
  pendingSyncFromPhase,
  resolveSyncPhase,
  SYNC_GRACE_MS,
  type ChapterSyncPhase,
} from '@/utils/chapterSyncPhase'
import {
  CHAPTER_BACKUP_KEY_PREFIX,
  STORAGE_METADATA_KEY,
  chapterBackupKey,
  chapterDiscardedSnapshotKey,
  chapterServerConflictSnapshotKey,
  isPrimaryChapterBackupStorageKey,
  isStorageObjectId,
  parseChapterBackupKey,
  type ChapterBackupRecord,
  type StorageAdapter,
  type StorageMetadata,
} from './types'

const MAX_GRACE_MIRRORS = 10
const STORAGE_VERSION = '2.0'

/** Vue reactive proxies cannot be structured-cloned into IndexedDB or BroadcastChannel. */
function plainJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function resolvePatchScope(
  incoming?: 'title' | 'full',
  existing?: 'title' | 'full',
): 'title' | 'full' | undefined {
  if (incoming === 'full') return 'full'
  if (incoming === 'title') {
    return existing === 'full' ? 'full' : 'title'
  }
  return existing
}

export type ChapterBackupWriteInput = Omit<
  ChapterBackupRecord,
  'timestamp' | 'savedAt' | 'sourceId'
>

/**
 * Chapter offline backup repository.
 * Keeps a sync in-memory cache so BackupService can keep a stable sync API,
 * while persisting asynchronously through a StorageAdapter (IndexedDB by default).
 */
export class ChapterBackupRepository {
  private adapter: StorageAdapter = createMemoryAdapter()
  private readonly cache = new Map<string, ChapterBackupRecord>()
  private readonly tabSourceId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `tab-${Date.now()}`
  private initPromise: Promise<void> | null = null
  private ready = false
  private unsubscribeBroadcast: (() => void) | null = null

  /** Stable per-tab source id for multi-tab awareness. */
  getSourceId(): string {
    return this.tabSourceId
  }

  generateKey(projectId: string, chapterId: string): string {
    return chapterBackupKey(projectId, chapterId)
  }

  /**
   * Kick off async hydration. Safe to call multiple times.
   * Immediately seeds the cache from any remaining legacy localStorage backups
   * so sync reads work before IndexedDB finishes loading.
   */
  initialize(): void {
    this.seedCacheFromLegacyLocalStorage()
    if (!this.initPromise) {
      this.initPromise = this.initializeAsync()
    }
  }

  /** Awaitable init for tests or callers that need readiness. */
  whenReady(): Promise<void> {
    this.initialize()
    return this.initPromise ?? Promise.resolve()
  }

  isReady(): boolean {
    return this.ready
  }

  isStorageAvailable(): boolean {
    return typeof indexedDB !== 'undefined' || typeof localStorage !== 'undefined'
  }

  save(backup: ChapterBackupWriteInput): ChapterBackupRecord {
    const key = chapterBackupKey(backup.projectId, backup.chapterId)
    const existing = this.cache.get(key)
    const pendingSync = backup.pendingSync !== false
    const retained =
      backup.retained !== undefined ? backup.retained === true : existing?.retained === true
    const syncPhase: ChapterSyncPhase =
      backup.syncPhase
      || (pendingSync
        ? 'dirty'
        : retained
          ? 'offline_mirror'
          : 'synced_grace')

    const record: ChapterBackupRecord = {
      projectId: backup.projectId,
      chapterId: backup.chapterId,
      title: backup.title || '',
      content: backup.content,
      jsonContent: plainJson(backup.jsonContent || {}),
      timestamp: Date.now(),
      savedAt: new Date().toISOString(),
      sourceId: this.tabSourceId,
      baseUpdatedAt: backup.baseUpdatedAt,
      baseContentVersion: backup.baseContentVersion,
      inDoubtSaveIds: backup.inDoubtSaveIds
        ? [...backup.inDoubtSaveIds]
        : existing?.inDoubtSaveIds,
      pendingSync,
      syncPhase,
      lastSuccessfulSyncAt: backup.lastSuccessfulSyncAt ?? existing?.lastSuccessfulSyncAt,
      lastErrorCode: backup.lastErrorCode,
      lastErrorMessage: backup.lastErrorMessage,
      retained,
      order: backup.order !== undefined ? backup.order : existing?.order,
      patchScope: resolvePatchScope(backup.patchScope, existing?.patchScope),
      userId: backup.userId || existing?.userId,
    }

    this.cache.set(key, record)
    void this.persistPut(key, record)
    publishChapterBackupPut(record.projectId, record.chapterId, record)
    return record
  }

  /** Archived local text before applying server content; does not use the main backup slot. */
  saveDiscardedSnapshot(backup: ChapterBackupWriteInput): ChapterBackupRecord | null {
    const userId = backup.userId
    if (!userId) {
      return null
    }

    const key = chapterDiscardedSnapshotKey(backup.projectId, backup.chapterId)
    const record: ChapterBackupRecord = {
      projectId: backup.projectId,
      chapterId: backup.chapterId,
      title: backup.title || '',
      content: backup.content,
      jsonContent: plainJson(backup.jsonContent || {}),
      timestamp: Date.now(),
      savedAt: new Date().toISOString(),
      sourceId: this.tabSourceId,
      baseUpdatedAt: backup.baseUpdatedAt,
      pendingSync: false,
      syncPhase: 'offline_mirror',
      lastSuccessfulSyncAt: Date.now(),
      retained: true,
      order: backup.order,
      userId,
    }

    this.cache.set(key, record)
    void this.persistPut(key, record)
    return record
  }

  saveServerConflictSnapshot(backup: ChapterBackupWriteInput): ChapterBackupRecord | null {
    const userId = backup.userId
    if (!userId) {
      return null
    }

    const key = chapterServerConflictSnapshotKey(backup.projectId, backup.chapterId)
    const record: ChapterBackupRecord = {
      projectId: backup.projectId,
      chapterId: backup.chapterId,
      title: backup.title || '',
      content: backup.content,
      jsonContent: plainJson(backup.jsonContent || {}),
      timestamp: Date.now(),
      savedAt: new Date().toISOString(),
      sourceId: this.tabSourceId,
      baseUpdatedAt: backup.baseUpdatedAt,
      baseContentVersion: backup.baseContentVersion,
      pendingSync: false,
      syncPhase: 'offline_mirror',
      lastSuccessfulSyncAt: Date.now(),
      retained: true,
      order: backup.order,
      userId,
    }

    this.cache.set(key, record)
    void this.persistPut(key, record)
    return record
  }

  get(projectId: string, chapterId: string): ChapterBackupRecord | null {
    if (!this.ready) {
      this.seedCacheFromLegacyLocalStorage()
    }
    return this.cache.get(chapterBackupKey(projectId, chapterId)) ?? null
  }

  /** Find a backup by chapter id when project id is unknown (at most one match). */
  findByChapterId(chapterId: string): ChapterBackupRecord | null {
    if (!this.ready) {
      this.seedCacheFromLegacyLocalStorage()
    }
    for (const record of this.cache.values()) {
      if (record.chapterId === chapterId) {
        return record
      }
    }
    return null
  }

  /**
   * Merge fields into an existing backup without bumping edit timestamp unless requested.
   */
  patch(
    projectId: string,
    chapterId: string,
    updates: Partial<ChapterBackupRecord> & { bumpTimestamp?: boolean },
  ): ChapterBackupRecord | null {
    const key = chapterBackupKey(projectId, chapterId)
    const existing = this.cache.get(key)
    if (!existing) return null

    const next: ChapterBackupRecord = {
      ...existing,
      ...updates,
      projectId,
      chapterId,
      timestamp: updates.bumpTimestamp ? Date.now() : existing.timestamp,
      savedAt: updates.bumpTimestamp ? new Date().toISOString() : existing.savedAt,
      sourceId: this.tabSourceId,
    }

    if (updates.syncPhase) {
      next.pendingSync = pendingSyncFromPhase(resolveSyncPhase(next))
    }

    this.cache.set(key, next)
    void this.persistPut(key, next)
    publishChapterBackupPut(next.projectId, next.chapterId, next)
    return next
  }

  remove(projectId: string, chapterId: string): void {
    const key = chapterBackupKey(projectId, chapterId)
    this.cache.delete(key)
    void this.persistDelete(key)
    publishChapterBackupRemove(projectId, chapterId)
  }

  cleanupProject(projectId: string): void {
    const prefix = `${CHAPTER_BACKUP_KEY_PREFIX}${projectId}:`
    const keys = Array.from(this.cache.keys()).filter((key) => key.startsWith(prefix))
    for (const key of keys) {
      const parsed = parseChapterBackupKey(key)
      this.cache.delete(key)
      void this.persistDelete(key)
      if (parsed) {
        publishChapterBackupRemove(parsed.projectId, parsed.chapterId)
      }
    }
  }

  async clearAll(): Promise<void> {
    await this.whenReady()
    const adapterKeys = await this.adapter.listKeys(CHAPTER_BACKUP_KEY_PREFIX)
    const cacheKeys = Array.from(this.cache.keys()).filter((key) =>
      key.startsWith(CHAPTER_BACKUP_KEY_PREFIX),
    )
    const keys = new Set([...adapterKeys, ...cacheKeys])
    for (const key of keys) {
      const parsed = parseChapterBackupKey(key)
      this.cache.delete(key)
      await this.adapter.delete(key)
      if (parsed) {
        publishChapterBackupRemove(parsed.projectId, parsed.chapterId)
      }
    }
    this.cache.clear()
    await this.adapter.delete(STORAGE_METADATA_KEY)
  }

  getAllBackupKeys(): string[] {
    return Array.from(this.cache.keys())
  }

  listAll(): ChapterBackupRecord[] {
    return Array.from(this.cache.entries())
      .filter(([key]) => isPrimaryChapterBackupStorageKey(key))
      .map(([, record]) => record)
  }

  /** Reassign ownership without changing backup timestamps or sync state. */
  retagUser(fromUserId: string, toUserId: string): void {
    if (!fromUserId || !toUserId || fromUserId === toUserId) return

    for (const [key, record] of this.cache.entries()) {
      if (record.userId !== fromUserId) continue
      const next: ChapterBackupRecord = { ...record, userId: toUserId }
      this.cache.set(key, next)
      this.persistPut(key, next)
      publishChapterBackupPut(next.projectId, next.chapterId, next)
    }
  }

  private seedCacheFromLegacyLocalStorage(): void {
    const legacy = readLegacyLocalStorageBackups()
    for (const record of legacy) {
      const key = chapterBackupKey(record.projectId, record.chapterId)
      const existing = this.cache.get(key)
      if (!existing || record.timestamp >= existing.timestamp) {
        this.cache.set(key, record)
      }
    }
  }

  private async initializeAsync(): Promise<void> {
    try {
      if (await isIndexedDbAvailable()) {
        this.adapter = createIndexedDbAdapter()
      } else {
        console.warn('IndexedDB unavailable; using in-memory chapter backup storage')
        this.adapter = createMemoryAdapter()
      }

      await migrateLocalStorageBackups(this.adapter)
      await this.hydrateCacheFromAdapter()
      await this.dropInvalidRecords()
      // Persist any early in-memory writes that predated IndexedDB readiness.
      await this.flushCacheToAdapter()
      await this.runCleanupIfNeeded()
      if (this.cache.size > 0) {
        void requestPersistentStorage()
      }

      if (!this.unsubscribeBroadcast) {
        this.unsubscribeBroadcast = subscribeChapterBackupBroadcast((message) => {
          if (message.type === 'put') {
            // Ignore echoes from this tab (same sourceId).
            if (message.backup.sourceId === this.tabSourceId) return
            const key = chapterBackupKey(message.projectId, message.chapterId)
            const existing = this.cache.get(key)
            if (!existing || message.backup.timestamp >= existing.timestamp) {
              this.cache.set(key, message.backup)
            }
            return
          }

          this.cache.delete(chapterBackupKey(message.projectId, message.chapterId))
        })
      }

      this.ready = true
    } catch (error) {
      console.error('Error initializing chapter backup repository:', error)
      this.ready = true
    }
  }

  private async dropInvalidRecords(): Promise<void> {
    const invalid = Array.from(this.cache.entries()).filter(
      ([, record]) =>
        !isStorageObjectId(record.projectId) || !isStorageObjectId(record.chapterId),
    )
    await Promise.all(
      invalid.map(async ([key]) => {
        this.cache.delete(key)
        await this.adapter.delete(key)
      }),
    )
  }

  private async hydrateCacheFromAdapter(): Promise<void> {
    const keys = await this.adapter.listKeys(CHAPTER_BACKUP_KEY_PREFIX)
    const records = await Promise.all(
      keys.map(async (key) => ({ key, record: await this.adapter.get<ChapterBackupRecord>(key) })),
    )
    for (const { key, record } of records) {
      if (record) {
        const migrated = this.migrateRecordSyncPhase(record)
        const existing = this.cache.get(key)
        if (!existing || migrated.timestamp >= existing.timestamp) {
          this.cache.set(key, migrated)
        }
      }
    }
  }

  private migrateRecordSyncPhase(record: ChapterBackupRecord): ChapterBackupRecord {
    if (record.syncPhase) {
      return record
    }
    const phase = resolveSyncPhase(record)
    return {
      ...record,
      syncPhase: phase,
      lastSuccessfulSyncAt:
        phase === 'synced_grace' || phase === 'offline_mirror'
          ? record.lastSuccessfulSyncAt ?? record.timestamp
          : record.lastSuccessfulSyncAt,
    }
  }

  private async flushCacheToAdapter(): Promise<void> {
    await Promise.all(
      Array.from(this.cache.entries()).map(([key, record]) => this.adapter.set(key, record)),
    )
  }

  private async runCleanupIfNeeded(): Promise<void> {
    const metadata =
      (await this.adapter.get<StorageMetadata>(STORAGE_METADATA_KEY)) ||
      ({
        lastCleanup: 0,
        version: STORAGE_VERSION,
      } satisfies StorageMetadata)

    const daysSinceCleanup =
      metadata.lastCleanup > 0
        ? (Date.now() - metadata.lastCleanup) / (24 * 60 * 60 * 1000)
        : Number.POSITIVE_INFINITY

    if (daysSinceCleanup < 1) {
      return
    }

    await this.cleanupOldBackups()
    await this.cleanupExcessBackups()

    const nextMetadata: StorageMetadata = {
      ...metadata,
      lastCleanup: Date.now(),
      version: STORAGE_VERSION,
      localStorageMigrated: true,
    }
    await this.adapter.set(STORAGE_METADATA_KEY, nextMetadata)
  }

  private isProtectedFromCleanup(record: ChapterBackupRecord): boolean {
    const phase = resolveSyncPhase(record)
    if (isActiveSyncPhase(phase) || phase === 'conflict' || phase === 'needs_editor') {
      return true
    }
    if (phase === 'offline_mirror' && record.retained === true) {
      return true
    }
    return false
  }

  private async cleanupOldBackups(): Promise<void> {
    const now = Date.now()
    const stale = Array.from(this.cache.entries()).filter(([, record]) => {
      if (this.isProtectedFromCleanup(record)) return false
      const phase = resolveSyncPhase(record)
      if (phase === 'synced_grace') {
        return isGraceExpired(record, now)
      }
      if (record.retained === true) return false
      const graceStart = record.lastSuccessfulSyncAt ?? record.timestamp
      return now - graceStart > SYNC_GRACE_MS
    })
    await Promise.all(
      stale.map(async ([key]) => {
        this.cache.delete(key)
        await this.adapter.delete(key)
      }),
    )
  }

  private async cleanupExcessBackups(): Promise<void> {
    const now = Date.now()
    const removable = Array.from(this.cache.entries()).filter(([, record]) => {
      if (this.isProtectedFromCleanup(record)) return false
      const phase = resolveSyncPhase(record)
      if (phase === 'synced_grace') {
        return isGraceExpired(record, now)
      }
      return record.retained !== true
    })
    if (removable.length <= MAX_GRACE_MIRRORS) return

    removable.sort((a, b) => b[1].timestamp - a[1].timestamp)
    const toRemove = removable.slice(MAX_GRACE_MIRRORS)
    await Promise.all(
      toRemove.map(async ([key]) => {
        this.cache.delete(key)
        await this.adapter.delete(key)
      }),
    )
  }

  private persistPut(key: string, record: ChapterBackupRecord): void {
    const write = (): Promise<void> => {
      if (this.ready) {
        return this.adapter.set(key, record)
      }
      return this.whenReady().then(() => this.adapter.set(key, record))
    }

    void write().catch((error) => {
      console.error('Error persisting chapter backup:', error)
    })
  }

  private async persistDelete(key: string): Promise<void> {
    try {
      await this.whenReady()
      await this.adapter.delete(key)
    } catch (error) {
      console.error('Error deleting chapter backup:', error)
    }
  }
}

let defaultRepository: ChapterBackupRepository | null = null

export function getChapterBackupRepository(): ChapterBackupRepository {
  if (!defaultRepository) {
    defaultRepository = new ChapterBackupRepository()
  }
  return defaultRepository
}

/** Test helper to inject a fresh repository instance. */
export function setChapterBackupRepositoryForTests(
  repository: ChapterBackupRepository | null,
): void {
  defaultRepository = repository
}
